import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
//redux imports and redirects
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setDeviceId } from "../../../reducers/idSlice";
import { redirectToDeviceOverview } from "app/common/redirects/Redirects";

import { PRODUCT_CONTEXT } from "common-ui-components/components/utils/Config";
import {
  openDiagnosticTool,
  openRemoteControlViewer,
} from "app/common/utils/remoteWindowUtils";
import {
  getDiagnosticToolOptions,
  normalizeDeviceOs,
  POWER_OPTIONS,
} from "app/common/config/deviceToolsConfig";
import { executePowerOption } from "app/common/utils/deviceToolUtils";

//notification imports
import { notify } from "common-ui-components/components/notification/Notification";
import {
  Success,
  Failure,
} from "common-ui-components/components/notification/templates";
import EM_API from "common-ui-components/components/utils/API";

import { isProduction } from "common-ui-components/components/utils/Config";

// Icon imports
import remoteControlIcon from "./icons/remote-control.svg";
import diagnosticsToolsIcon from "./icons/diagnostics-tools.svg";
import powerActionsIcon from "./icons/power-actions.svg";
import inventoryScanIcon from "./icons/inventory-scan.svg";
import patchScanIcon from "./icons/patch-scan.svg";
import inventoryDetailsIcon from "./icons/inventory-details.svg";
import patchDetailsIcon from "./icons/patch-details.svg";
import downArrowIcon from "./icons/right-arrow.svg";
import windowsGreenIcon from "./icons/os icons/windows-green.svg";
import windowsRedIcon from "./icons/os icons/windows.svg";
import appleGreenIcon from "./icons/os icons/apple-green.svg";
import appleRedIcon from "./icons/os icons/apple-red.svg";
import linuxGreenIcon from "./icons/os icons/linux-green.svg";
import linuxRedIcon from "./icons/os icons/linux-red.svg";

import styles from "./ManagedDevices.module.css";
import Message from "common-ui-components/components/messager/Message";
import { useRestrictionChecker } from "common-ui-components/components/restrictionUI/RestrictionChecker";

const DEVICE_ACTION_PRODUCT_ACCESS = {
  "Wake Up": { product: ["em", "em-msp", "ra", "ra-msp"] },
  "Remote Control": { product: ["em", "em-msp", "ra", "ra-msp"] },
  "Diagnostics Tools": { product: ["em", "em-msp", "ra", "ra-msp"] },
  "Power Actions": { product: ["em", "em-msp", "ra", "ra-msp"] },
  "Inventory Scan": { product: ["em", "em-msp", "am", "am-msp"] },
  "Inventory Details": {
    product: ["em", "em-msp", "am", "am-msp", "vm","pm"],
  },
  "Patch Scan": { product: ["em", "em-msp", "vm","pm"] },
  "Patch Details": { product: ["em", "em-msp", "vm","pm"] },
};

function CommonManagedDevice({
  record,
  children,
  loading,
  deviceIdAccessor = "id",
}) {
  const { checkRestriction } = useRestrictionChecker();
  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [hoveredItem, setHoveredItem] = useState("");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isTriggerHovered, setIsTriggerHovered] = useState(false);
  const [activeSubMenu, setActiveSubMenu] = useState("");
  const [isPowerOptionLoading, setIsPowerOptionLoading] = useState(false);
  const [isRemoteControlLoading, setIsRemoteControlLoading] = useState(false);
  const remoteControlTimerRef = useRef(null);
  const menuContainerRef = useRef(null);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const [confirmationState, setConfirmationState] = useState({
    isOpen: false,
    actionLabel: "",
    confirmLabel: "Confirm",
    type: "none",
  });
  const isRowActionLoading = isPowerOptionLoading || isRemoteControlLoading;
  const normalizedOs =
    `${record?.operatingSystem || record?.platform || ""}`.toLowerCase();
  const currentOs = normalizeDeviceOs(normalizedOs);
  const hidePatchActions =
    currentOs === "mac" || (currentOs === "linux" && isProduction);
  const isProductAvailable = ({ product }) => product.includes(PRODUCT_CONTEXT);
  const scanDeviceURL = {
    inventory: "/devices/on-demand-scan?scanLevel=DIFF",
    patch: "/patch/on-demand-scan",
  };

  const resolveOsStatusIcon = () => {
    const isAgentOnline = Boolean(record?.agentStatus);
    // const isAgentOnline=true

    if (normalizedOs.includes("mac")) {
      return isAgentOnline ? appleGreenIcon : appleRedIcon;
    }

    if (normalizedOs.includes("linux")) {
      return isAgentOnline ? linuxGreenIcon : linuxRedIcon;
    }

    return isAgentOnline ? windowsGreenIcon : windowsRedIcon;
  };

  const osStatusIcon = resolveOsStatusIcon();
  const isDeviceOnline = Boolean(record?.agentStatus);
  // const isDeviceOnline=true
  const deviceName =
    record?.deviceName || record?.name || record?.hostName || "this device";

  const openConfirmation = ({
    actionLabel,
    type,
    confirmLabel = "Confirm",
  }) => {
    setIsMenuOpen(false);
    setActiveSubMenu("");
    setConfirmationState({
      isOpen: true,
      actionLabel,
      confirmLabel,
      type,
    });
  };

  const handleSingleDevicePowerOption = async (option) => {
    setIsPowerOptionLoading(true);
    try {
      const response = await executePowerOption(
        record[deviceIdAccessor],
        option,
      );
      if (response.status.success) {
        notify(<Success>Power option {option} initiated successfully</Success>);
      } else {
        notify(<Failure>Failed in initiating power option {option}</Failure>);
      }
    } catch {
      notify(<Failure>Failed in initiating power option {option}</Failure>);
    } finally {
      setIsPowerOptionLoading(false);
    }
  };
  const handleToolsClick = (deviceId, toolName, os) => {
    if (checkRestriction("DIAGNOSTIC_TOOLS_CONNECT")) return;
    openDiagnosticTool({
      deviceId,
      toolName,
      os,
    });
  };

  const handleWakeUp = () => {
    const wakeUpDevice = async () => {
      try {
        const response = await EM_API.post("/devices/wake-devices-on-lan", {
          deviceIds: [record[deviceIdAccessor]],
        });

        if (response?.status?.success) {
          notify(<Success>Wake Up initiated successfully</Success>);
        } else {
          notify(
            <Failure>
              {response?.status?.message || "Failed to initiate Wake Up"}
            </Failure>,
          );
        }
      } catch (error) {
        console.error("Error initiating Wake Up", error);
        notify(<Failure>Failed to initiate Wake Up</Failure>);
      }
    };

    wakeUpDevice();
  };

  const handleSingleDeviceScan = async (scanType) => {
    const url = scanDeviceURL[scanType];
    if (!url) return;

    try {
      const response = await EM_API.post(url, {
        deviceIds: [record[deviceIdAccessor]],
      });

      if (response?.status?.success) {
        notify(
          <Success>
            {scanType === "inventory"
              ? "Inventory scan initiated successfully"
              : "Patch scan initiated successfully"}
          </Success>,
        );
      } else {
        notify(
          <Failure>
            {response?.status?.message || `Failed to initiate ${scanType} scan`}
          </Failure>,
        );
      }
    } catch (error) {
      console.error(`Error initiating ${scanType} scan`, error);
      notify(<Failure>{`Failed to initiate ${scanType} scan`}</Failure>);
    }
  };

  const handleConfirmAction = async () => {
    const { type, actionLabel } = confirmationState;
    setConfirmationState((prev) => ({ ...prev, isOpen: false }));

    if (type === "power") {
      await handleSingleDevicePowerOption(actionLabel);
      return;
    }

    if (type === "wakeUp") {
      handleWakeUp();
    }
  };

  const buildAppUrl = (path) => {
    const productPrefix = PRODUCT_CONTEXT ? `/${PRODUCT_CONTEXT}` : "";
    return `${window.location.origin}${productPrefix}${path}`;
  };

  const openInventoryDetails = () => {
    dispatch(setDeviceId(record[deviceIdAccessor]));
    window.open(buildAppUrl("/app/device-details/overview"), "_blank");
  };

  const openPatchDetails = () => {
    dispatch(setDeviceId(record[deviceIdAccessor]));
    window.open(
      buildAppUrl("/app/device-details/patches/missing"),
      "_blank",
    );
  };

  const buildDeviceActionMenu = () => {
    if (!isDeviceOnline) {
      return [
        {
          section: "offline-actions",
          items: [
            {
              name: "Wake Up",
              icon: powerActionsIcon,
              onClick: () =>
                openConfirmation({
                  actionLabel: "Wake Up",
                  type: "wakeUp",
                  confirmLabel: "Wake Up",
                }),
            },
          ],
        },
        {
          section: "details",
          items: [
            {
              name: "Inventory Details",
              icon: inventoryDetailsIcon,
              onClick: openInventoryDetails,
            },
            ...(!hidePatchActions
              ? [{
                  name: "Patch Details",
                  icon: patchDetailsIcon,
                  onClick: openPatchDetails,
                }]
              : []),
          ],
        },
      ].filter((section) => section.items.length > 0);
    }

    return [
      {
        section: "remote",
        items: [
          {
            name: "Remote Control",
            icon: remoteControlIcon,
            onClick: () => {
              if (!currentOs) {
                notify(
                  <Failure>
                    Unable to determine the device operating system.
                  </Failure>,
                );
                return;
              }
              openRemoteControlViewer({
                deviceId: record?.id,
                os: currentOs,
              });
            },
          },
          {
            name: "Diagnostics Tools",
            icon: diagnosticsToolsIcon,
            subOptions: getDiagnosticToolOptions(currentOs).map((toolName) => ({
              name: toolName,
              onClick: () =>
                handleToolsClick(record?.id, toolName, currentOs),
            })),
          },
          {
            name: "Power Actions",
            icon: powerActionsIcon,
            subOptions: POWER_OPTIONS.map((option) => ({
              name: option,
              onClick: () =>
                openConfirmation({
                  actionLabel: option,
                  type: "power",
                  confirmLabel: option,
                }),
            })),
          },
        ],
      },
      {
        section: "scan",
        items: [
          {
            name: "Inventory Scan",
            icon: inventoryScanIcon,
            onClick: () => handleSingleDeviceScan("inventory"),
          },
          ...(!hidePatchActions
            ? [{
                name: "Patch Scan",
                icon: patchScanIcon,
                onClick: () => handleSingleDeviceScan("patch"),
              }]
            : []),
        ],
      },
      {
        section: "details",
        items: [
          {
            name: "Inventory Details",
            icon: inventoryDetailsIcon,
            onClick: openInventoryDetails,
          },
          ...(!hidePatchActions
            ? [{
                name: "Patch Details",
                icon: patchDetailsIcon,
                onClick: openPatchDetails,
              }]
            : []),
        ],
      },
    ].filter((section) => section.items.length > 0);
  };

  const menuSections = buildDeviceActionMenu()
    .map((section) => ({
      ...section,
      items: section.items.map((item) => ({
        ...item,
        ...DEVICE_ACTION_PRODUCT_ACCESS[item.name],
        subOptions: item.subOptions
          ?.map((option) => ({
            ...option,
            ...DEVICE_ACTION_PRODUCT_ACCESS[item.name],
          }))
          .filter(isProductAvailable),
      })),
    }))
    .map((section) => ({
      ...section,
      items: section.items.filter(isProductAvailable),
    }))
    .filter((section) => section.items.length > 0);
  // Positioning logic:
  // - Opens below trigger when there is enough space.
  // - Flips above when near viewport bottom.
  // - Clamps horizontal position to keep menu inside viewport.
  const MENU_GAP = 2;
  const VIEWPORT_PADDING = 8;

  useEffect(() => {
    document.body.setAttribute(
      "data-disable-table-tooltip",
      isMenuOpen || isTriggerHovered || confirmationState.isOpen
        ? "true"
        : "false",
    );

    return () => {
      document.body.setAttribute("data-disable-table-tooltip", "false");
    };
  }, [isMenuOpen, isTriggerHovered, confirmationState.isOpen]);

  useEffect(() => {
    return () => {
      if (remoteControlTimerRef.current) {
        clearTimeout(remoteControlTimerRef.current);
      }
    };
  }, []);

  useLayoutEffect(() => {
    if (!isMenuOpen) return;

    const updateMenuPosition = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const menuWidth = menuRef.current?.offsetWidth ?? 240;
      const menuHeight = menuRef.current?.offsetHeight ?? 320;
      const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_PADDING;
      const spaceAbove = rect.top - VIEWPORT_PADDING;

      let left = rect.left;
      left = Math.min(left, window.innerWidth - menuWidth - VIEWPORT_PADDING);
      left = Math.max(VIEWPORT_PADDING, left);

      let top;
      if (spaceBelow >= menuHeight || spaceBelow >= spaceAbove) {
        top = rect.bottom + MENU_GAP;
        top = Math.min(top, window.innerHeight - menuHeight - VIEWPORT_PADDING);
      } else {
        top = rect.top - menuHeight - MENU_GAP;
        top = Math.max(VIEWPORT_PADDING, top);
      }

      setMenuPosition({
        top: Math.round(top),
        left: Math.round(left),
      });
    };

    updateMenuPosition();

    const handleOutsideClick = (event) => {
      const clickedInsideTrigger = menuContainerRef.current?.contains(
        event.target,
      );
      const clickedInsideMenu = menuRef.current?.contains(event.target);
      if (!clickedInsideTrigger && !clickedInsideMenu) {
        setIsMenuOpen(false);
      }
    };

    const handleScrollClose = () => {
      setIsMenuOpen(false);
    };

    const handleResize = () => {
      updateMenuPosition();
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("scroll", handleScrollClose, true);
    window.addEventListener("scroll", handleScrollClose, true);
    window.addEventListener("resize", handleResize);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("scroll", handleScrollClose, true);
      window.removeEventListener("scroll", handleScrollClose, true);
      window.removeEventListener("resize", handleResize);
    };
  }, [isMenuOpen]);

  const handleClick = () => {
    if (isMenuOpen || confirmationState.isOpen) return;

    dispatch(setDeviceId(record[deviceIdAccessor]));
    redirectToDeviceOverview(navigate);
  };

  return (
    <div ref={menuContainerRef} onClick={handleClick} className={styles.root}>
      <div className={styles.row}>
        <button
          type="button"
          ref={triggerRef}
          className={[
            styles.deviceIcons,
            styles.triggerButton,
            isMenuOpen ? styles.triggerButtonActive : "",
          ]
            .filter(Boolean)
            .join(" ")}
          onMouseEnter={() => setIsTriggerHovered(true)}
          onMouseLeave={() => setIsTriggerHovered(false)}
          onClick={(event) => {
            event.stopPropagation();
            event.preventDefault();
            setIsMenuOpen((prev) => {
              if (prev) return false;
              setActiveSubMenu("");
              const rect = triggerRef.current?.getBoundingClientRect();
              if (rect) {
                setMenuPosition({
                  top: Math.round(rect.bottom + MENU_GAP),
                  left: Math.round(rect.left),
                });
              }
              return true;
            });
          }}
        >
          {isRowActionLoading ? (
            <span
              className={[styles.optionSpinner, styles.triggerSpinner].join(
                " ",
              )}
              aria-hidden="true"
            />
          ) : (
            <>
              <img src={osStatusIcon} alt="" className={styles.osIcon} />
              <img src={downArrowIcon} alt="" className={styles.triggerIcon} />
            </>
          )}
        </button>
        <div className={styles.deviceName}>{children}</div>
      </div>
      {isMenuOpen &&
        menuPosition &&
        createPortal(
          <div
            ref={menuRef}
            className={styles.menu}
            style={{ top: menuPosition.top, left: menuPosition.left }}
          >
            {menuSections.map((section, sectionIndex) => (
              <div key={section.section}>
                {sectionIndex > 0 && <div className={styles.menuDivider} />}
                {section.items.map((item) => (
                  <div key={item.name} className={styles.menuItemWrapper}>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        if (item.name === "Remote Control") {
                          if (
                            loading ||
                            isPowerOptionLoading ||
                            isRemoteControlLoading
                          ) {
                            return;
                          }
                          setIsRemoteControlLoading(true);
                          try {
                            item.onClick?.();
                          } finally {
                            setIsRemoteControlLoading(false);
                            setIsMenuOpen(false);
                          }
                          return;
                        }
                        if (item.subOptions) {
                          if (!loading && !isPowerOptionLoading) {
                            setActiveSubMenu((prev) =>
                              prev === item.name ? "" : item.name,
                            );
                          }
                          return;
                        }
                        if (!loading) item.onClick();
                        setIsMenuOpen(false);
                      }}
                      onMouseEnter={() => setHoveredItem(item.name)}
                      onMouseLeave={() => setHoveredItem("")}
                      className={[
                        styles.menuItem,
                        hoveredItem === item.name ? styles.menuItemHover : "",
                        loading ? styles.menuItemDisabled : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      <span className={styles.menuIcon}>
                        <img src={item.icon} alt="" />
                      </span>
                      <span className={styles.menuLabel}>{item.name}</span>
                      {item.subOptions && (
                        <img
                          src={downArrowIcon}
                          alt=""
                          aria-hidden="true"
                          className={[
                            styles.menuArrowIcon,
                            activeSubMenu === item.name
                              ? styles.menuArrowIconOpen
                              : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                        />
                      )}
                    </button>
                    {item.subOptions && activeSubMenu === item.name && (
                      <div className={styles.subMenu}>
                        {item.subOptions.map((subOption) => (
                          <button
                            key={subOption.name}
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              if (!loading && !isPowerOptionLoading)
                                subOption.onClick();
                            }}
                            onMouseEnter={() => setHoveredItem(subOption.name)}
                            onMouseLeave={() => setHoveredItem("")}
                            className={[
                              styles.subMenuItem,
                              hoveredItem === subOption.name
                                ? styles.menuItemHover
                                : "",
                              loading || isPowerOptionLoading
                                ? styles.menuItemDisabled
                                : "",
                            ]
                              .filter(Boolean)
                              .join(" ")}
                          >
                            <span>{subOption.name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>,
          document.body,
        )}
      <Message
        isOpen={confirmationState.isOpen}
        onClose={() =>
          setConfirmationState((prev) => ({ ...prev, isOpen: false }))
        }
        title={`${confirmationState.actionLabel} Confirmation`}
        description={`Are you sure you want to ${confirmationState.actionLabel.toLowerCase()} ${deviceName}?`}
        onConfirm={handleConfirmAction}
        confirmMessage={confirmationState.confirmLabel}
        onCancel={() =>
          setConfirmationState((prev) => ({ ...prev, isOpen: false }))
        }
        loading={isPowerOptionLoading}
      />
    </div>
  );
}

export default CommonManagedDevice;
